import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  signal,
  OnInit,
  OnDestroy,
  inject,
  TemplateRef,
  viewChild,
  ChangeDetectorRef,
  computed,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";

import { Subscription } from "rxjs";

import { ConfirmDialogComponent } from "@components/shared";
import { InputSelectOption } from "@components/shared/inputs/input-select/input-select.model";
import { CopyModeEnum } from "@components/shared/value-and-copy-button/value-and-copy-button.model";
import { IconConstants, TextConstants } from "@shared/constants";
import {
  ConfirmDialogResponseEnum,
  RulesetBooleanOperatorEnum,
  RulesetConditionOperatorEnum,
  TargetPathEnum,
} from "@shared/enums";
import { IConditionItemForm } from "@shared/interfaces";
import { AuthenticationService, LocationTypesService } from "@shared/services";

interface ValueComponentOptions {
  options: InputSelectOption[];
  allowCreateTag?: boolean;
  tagUrl?: string;
  viewModeTemplate?: TemplateRef<unknown>;
  tagCreatedEvent?: (option: InputSelectOption) => void;
}

@Component({
  standalone: false,
  selector: "app-ruleset-criteria-item",
  templateUrl: "./ruleset-criteria-item.component.html",
  styleUrl: "./ruleset-criteria-item.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RulesetCriteriaItemComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);

  private locationTypesService = inject(LocationTypesService);

  private authenticationService = inject(AuthenticationService);

  private cdr = inject(ChangeDetectorRef);

  private readonly activeOrganisationId = this.authenticationService.getActiveOrganisationId();

  private subscriptions = new Subscription();

  countryViewTemplate = viewChild<TemplateRef<unknown>>("countryViewTemplate");

  formGroup = input.required<FormGroup<IConditionItemForm>>();

  shouldDisplayOptions = input.required<boolean>();

  propertyOptions = input.required<InputSelectOption[]>();

  shouldBeIndented = input.required<boolean>();

  isReadOnly = input<boolean>(false);

  countryOptions = input<InputSelectOption[]>([]);

  locationOptions = input<InputSelectOption[]>([]);

  locationTypeOptions = input<InputSelectOption[]>([]);

  booleanOperator = input<string>(null);

  remove = output<void>();

  convertToGroup = output<void>();

  propertyType = signal<"country" | "locationType" | "location">("locationType");

  readonly valueComponentOptions = signal<ValueComponentOptions>(null);

  readonly copyMode = signal<CopyModeEnum>(CopyModeEnum.NONE);

  booleanOperatorText = computed(() => {
    return this.booleanOperator() === RulesetBooleanOperatorEnum.OR
      ? $localize`:@@shared.or:Or`
      : $localize`:@@shared.and:And`;
  });

  readonly operatorOptions = computed(() => [
    {
      label: this.toLowerCaseWhenReadOnly($localize`:@@ruleset.is:Is`),
      value: RulesetConditionOperatorEnum.EQ,
    },
    {
      label: this.toLowerCaseWhenReadOnly($localize`:@@ruleset.isanyof:Is any of`),
      value: RulesetConditionOperatorEnum.ANY,
    },
    {
      label: this.toLowerCaseWhenReadOnly($localize`:@@ruleset.none:Is none of`),
      value: RulesetConditionOperatorEnum.NONE,
    },
  ]);

  readonly translations = {
    deleteCriteria: $localize`:@@ruleset.deletecriteria:Delete criteria`,
    convertToGroup: $localize`:@@ruleset.converttocriteria:Convert to criteria group`,
    property: $localize`:@@ruleset.selectproperty:Select property`,
    operator: $localize`:@@ruleset.selectoperator:Select operator`,
    value: $localize`:@@ruleset.selectvalue:Select value(s)`,
  };

  readonly isEqOperator = signal(true);

  ngOnInit() {
    this.setPropertyType(this.formGroup().get("path")?.value);

    this.subscriptions.add(
      this.formGroup()
        .get("path")
        .valueChanges.subscribe((value: string) => {
          this.cdr.detectChanges();
          this.setPropertyType(value);
          this.formGroup().get("value").setValue(null, { emitEvent: false });
        }),
    );

    this.subscriptions.add(
      this.formGroup()
        .get("operator")
        .valueChanges.subscribe((value) => {
          const newValue = value === RulesetConditionOperatorEnum.EQ ? null : [];

          this.formGroup().get("value").setValue(newValue);
          this.setIsEqOperator(value);
        }),
    );

    this.setIsEqOperator(this.formGroup().get("operator").value);
  }

  private setIsEqOperator(value: RulesetConditionOperatorEnum) {
    this.isEqOperator.set(value === RulesetConditionOperatorEnum.EQ);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onConvertToGroup() {
    this.convertToGroup.emit();
  }

  private toLowerCaseWhenReadOnly = (value: string) =>
    this.isReadOnly() ? value.toLowerCase() : value;

  private setPropertyType(value: string) {
    if (!value) {
      this.valueComponentOptions.set({
        options: [],
      });

      this.formGroup().get("value").disable();

      return;
    }

    if (value.includes(TargetPathEnum.COUNTRY)) {
      this.propertyType.set("country");

      this.valueComponentOptions.set({
        options: this.countryOptions(),
        viewModeTemplate: this.countryViewTemplate(),
      });
    } else if (value.includes(TargetPathEnum.TYPES)) {
      this.propertyType.set("locationType");

      this.valueComponentOptions.set({
        options: this.locationTypeOptions(),
        allowCreateTag: true,
        tagUrl: this.locationTypesService.getBaseUrl,
        tagCreatedEvent: this.onNewLocationTypeCreated.bind(this),
      });
    } else {
      this.propertyType.set("location");

      this.valueComponentOptions.set({
        options: this.locationOptions(),
      });
    }

    if (!this.isReadOnly()) {
      this.formGroup().get("value").enable();
    }
  }

  isArray(value: string) {
    return Array.isArray(value);
  }

  onRemove() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: $localize`:@@ruleset.deletecriteria:Delete criteria`,
        contentText: $localize`:@@ruleset.deletecriteriaconfirm:Are you sure you want to delete the criteria?`,
        confirmButtonColor: "danger",
        confirmButtonText: TextConstants.DELETE,
        confirmButtonIcon: IconConstants.DELETE,
      },
    });

    dialogRef.afterClosed().subscribe((result: ConfirmDialogResponseEnum) => {
      if (result === ConfirmDialogResponseEnum.CONFIRM) {
        this.remove.emit();
      }
    });
  }

  onNewLocationTypeCreated(option: InputSelectOption) {
    option.value = `/organisations/${this.activeOrganisationId}/location-types/${option.value}`;
  }
}
